import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Trash2, ShieldCheck, User as UserIcon, Loader2, KeyRound, Clock, Mail, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const INVITE_BADGE = {
  attivo: { label: "Attivo", cls: "border-emerald-500/40 text-emerald-500 bg-emerald-500/10" },
  in_attesa: { label: "Invito in attesa", cls: "border-amber-500/40 text-amber-500 bg-amber-500/10" },
  scaduto: { label: "Invito scaduto", cls: "border-red-500/40 text-red-500 bg-red-500/10" },
  mai_invitato: { label: "Mai attivato", cls: "border-border text-muted-foreground" },
};

const InviteBadge = ({ u, fmt }) => {
  const b = INVITE_BADGE[u.invite_status] || INVITE_BADGE.mai_invitato;
  return (
    <div className="mt-2" data-testid={`invite-status-${u.email}`}>
      <Badge variant="outline" className={`text-[10px] ${b.cls}`}>{b.label}</Badge>
      {u.invite_status !== "attivo" && u.invite_sent_at && (
        <div className="text-[11px] text-muted-foreground font-mono mt-1" data-testid={`invite-sent-${u.email}`}>Invito inviato: {fmt(u.invite_sent_at)}</div>
      )}
    </div>
  );
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);

  const fmtLogin = (iso) => {
    if (!iso) return "Mai effettuato";
    try {
      return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch (e) { return iso; }
  };

  const doReset = async () => {
    if (resetPw.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setResetting(true);
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password di ${resetFor.email} reimpostata`);
      setResetFor(null); setResetPw("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  const [sendingReset, setSendingReset] = useState(null);
  const sendResetEmail = async (u) => {
    setSendingReset(u.id);
    try {
      await api.post(`/users/${u.id}/send-reset`);
      toast.success(`Email di reset inviata a ${u.email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingReset(null);
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const [sendingWelcome, setSendingWelcome] = useState(null);
  const sendWelcome = async (u) => {
    setSendingWelcome(u.id);
    try {
      await api.post(`/users/${u.id}/send-welcome`);
      toast.success(`Email di benvenuto inviata a ${u.email}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendingWelcome(null);
    }
  };

  const create = async () => {
    if (!form.email) { toast.error("Inserisci l'email"); return; }
    if (form.password && form.password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/users", form);
      if (data.email_sent) toast.success(`Utente ${form.email} creato. Email di benvenuto inviata.`);
      else toast.warning(`Utente ${form.email} creato, ma l'email di benvenuto non è partita. Usa "Reinvia email di benvenuto".`);
      setOpen(false);
      setForm({ email: "", name: "", password: "", role: "user" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utente eliminato");
      setUsers((u) => u.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Area Riservata</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Gestione Utenti</h1>
          <p className="text-sm text-muted-foreground mt-2">Crea e gestisci gli accessi al portale. Solo l'amministratore può gestire gli utenti.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn"><Plus className="h-4 w-4 mr-2" /> Nuovo Utente</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuovo Utente</DialogTitle>
              <DialogDescription>L'utente riceverà un'email di benvenuto con un link personale (valido 48 ore) per scegliere la password.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="ue">Email</Label>
                <Input id="ue" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="bruno@trustmaster.it" data-testid="user-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="un">Nome</Label>
                <Input id="un" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bruno" data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="up">Password iniziale (opzionale)</Label>
                <Input id="up" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Lascia vuoto: la sceglie l'utente dal link" data-testid="user-password-input" />
              </div>
              <div className="space-y-2">
                <Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user" data-testid="role-user">Utente</SelectItem>
                    <SelectItem value="admin" data-testid="role-admin">Amministratore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} data-testid="save-user-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Crea Utente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((u) => (
            <Card key={u.id} className="p-5 flex items-start justify-between" data-testid={`user-card-${u.email}`}>
              <div className="flex items-start gap-3">
                <div className={`grid place-items-center h-10 w-10 rounded-lg ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-secondary text-secondary-foreground"}`}>
                  {u.role === "admin" ? <ShieldCheck className="h-5 w-5" /> : <UserIcon className="h-5 w-5" />}
                </div>
                <div>
                  <div className="font-medium">{u.name || "—"}</div>
                  <div className="text-sm text-muted-foreground font-mono">{u.email}</div>
                  <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground font-mono">
                    <Clock className="h-3 w-3" /> Ultimo accesso: {fmtLogin(u.last_login)}
                  </div>
                  <Badge variant="outline" className="text-[10px] mt-2">{u.role === "admin" ? "Amministratore" : "Utente"}</Badge>
                  {u.id === user?.id && <Badge variant="outline" className="text-[10px] mt-2 ml-1 border-primary/40 text-primary">Tu</Badge>}
                  <InviteBadge u={u} fmt={fmtLogin} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="text-muted-foreground hover:text-primary transition-colors" data-testid={`send-welcome-email-${u.email}`}
                  onClick={() => sendWelcome(u)} title="Reinvia email di benvenuto" disabled={sendingWelcome === u.id}>
                  {sendingWelcome === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
                <button className="text-muted-foreground hover:text-primary transition-colors" data-testid={`send-reset-email-${u.email}`}
                  onClick={() => sendResetEmail(u)} title="Invia email di reset password" disabled={sendingReset === u.id}>
                  {sendingReset === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                </button>
                <button className="text-muted-foreground hover:text-primary transition-colors" data-testid={`reset-password-${u.email}`}
                  onClick={() => { setResetFor(u); setResetPw(""); }} title="Imposta password manualmente">
                  <KeyRound className="h-4 w-4" />
                </button>
                {u.id !== user?.id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="text-muted-foreground hover:text-red-500 transition-colors" data-testid={`delete-user-${u.email}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare {u.email}?</AlertDialogTitle>
                        <AlertDialogDescription>L'utente non potrà più accedere al portale.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(u.id)} data-testid={`confirm-delete-user-${u.email}`}>Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) { setResetFor(null); setResetPw(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Reimposta Password</DialogTitle>
            <DialogDescription>Imposta una nuova password per {resetFor?.email}. L'utente potrà cambiarla dal proprio profilo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="rp">Nuova password</Label>
            <Input id="rp" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="min. 6 caratteri" data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button onClick={doReset} disabled={resetting} data-testid="reset-password-save-btn">
              {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Reimposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
